import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import { Close, LocalShipping, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { btnSx } from '../styles';

const ShippingDialog = ({ open, onClose, apiCaller, winner, onSaved }) => {
  const [trackingId, setTrackingId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (winner) {
      setTrackingId(winner.shipping?.trackingId || '');
      setVendorName(winner.shipping?.vendorName || '');
      setStatus(winner.shipping?.status || 'pending');
      setNotes(winner.shipping?.notes || '');
    }
  }, [winner]);

  const handleSave = async () => {
    if (!trackingId.trim() || !vendorName.trim()) {
      toast.error('Tracking ID and Vendor Name are required');
      return;
    }
    setSaving(true);
    try {
      await apiCaller.put(`/super-user/dyk/winners/${winner._id}/shipping`, {
        trackingId,
        vendorName,
        status,
        notes,
      });
      toast.success('Shipping details saved');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save shipping details');
    } finally {
      setSaving(false);
    }
  };

  if (!winner) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LocalShipping sx={{ color: '#6366f1' }} />
          <Typography fontWeight={700}>Prize Shipping Details</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography fontWeight={700} fontSize="0.9rem">
            #{winner.rank} — {winner.employeeName}
          </Typography>
          <Typography fontSize="0.8rem" color="text.secondary">
            Emp ID: {winner.empId} · Region: {winner.regionName} ({winner.regionId || '—'})
          </Typography>
        </Box>
        <Stack spacing={2}>
          <TextField
            label="Tracking ID"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="Courier / Vendor Name (e.g. DHL, BlueDart)"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            fullWidth
            size="small"
            required
          />
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={btnSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
          sx={{ ...btnSx, background: '#6366f1' }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShippingDialog;
